import React, { useState } from 'react';
import { Box, Collapse, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { getFieldDescription } from 'components/utils';
import { insetBgSx, surfaceBorderSx, surfaceCardSx, SurfaceHostContext } from 'components/common/surfaces';

const Group = ({
  id,
  children,
  title,
  titleIcon,
  action,
  bg = 'white',
  boredered: _boredered = false,
  required = false,
  isCollapsible = false,
  defaultExpanded = true,
  'data-testid': dataTestId,
}: {
  id?: string;
  children: React.ReactNode;
  title?: string;
  titleIcon?: React.ReactNode;
  action?: React.ReactNode;
  bg?: 'white' | 'gray';
  boredered?: boolean;
  'data-testid'?: string;
  required?: boolean;
  isCollapsible?: boolean;
  defaultExpanded?: boolean;
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const showHeader = Boolean(title) || isCollapsible || Boolean(action);
  const isPaperSurface = bg !== 'gray';

  const toggleExpanded = () => setIsExpanded(expanded => !expanded);

  const body = (
    <Box
      data-testid={dataTestId}
      sx={{
        ...(isPaperSurface ? surfaceCardSx : { ...insetBgSx, ...surfaceBorderSx }),
        p: 3,
        mb: 2,
      }}
    >
      {showHeader ? (
        <Box
          {...(isCollapsible
            ? {
                role: 'button' as const,
                tabIndex: 0,
                'aria-expanded': isExpanded,
                'aria-label': isExpanded ? 'Collapse section' : 'Expand section',
                onClick: toggleExpanded,
                onKeyDown: (event: React.KeyboardEvent) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    toggleExpanded();
                  }
                },
              }
            : {})}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            ...(isCollapsible
              ? {
                  cursor: 'pointer',
                  userSelect: 'none',
                  // Expand hit area into the card padding so the whole title band is clickable.
                  mx: -3,
                  mt: -3,
                  px: 3,
                  pt: 3,
                  mb: isExpanded ? 1.5 : -3,
                  pb: isExpanded ? 0 : 3,
                }
              : { mb: 1.5 }),
          }}
        >
          {titleIcon ? <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>{titleIcon}</Box> : null}

          {title ? (
            <Typography
              variant="h6"
              sx={{
                fontWeight: 'bold',
                color: 'text.primary',
                fontSize: '16px',
                lineHeight: 1.2,
              }}
            >
              {title}
            </Typography>
          ) : null}

          {required ? (
            <Typography variant="caption" color="error">
              *
            </Typography>
          ) : null}

          <Box sx={{ alignSelf: 'flex-end' }}>{id ? getFieldDescription(id, true) : null}</Box>

          {action ? (
            <Box
              sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}
              onClick={isCollapsible ? event => event.stopPropagation() : undefined}
            >
              {action}
            </Box>
          ) : null}

          {isCollapsible ? (
            <KeyboardArrowDownIcon
              fontSize="small"
              sx={{
                ml: action ? 0 : 'auto',
                color: 'action.active',
                transform: isExpanded ? 'rotate(180deg)' : 'none',
              }}
            />
          ) : null}
        </Box>
      ) : null}

      {isCollapsible ? (
        <Collapse in={isExpanded} unmountOnExit>
          {children}
        </Collapse>
      ) : (
        children
      )}
    </Box>
  );

  if (!isPaperSurface) {
    return body;
  }

  return <SurfaceHostContext.Provider value={true}>{body}</SurfaceHostContext.Provider>;
};

export default Group;
